/**
 * useRecords Hook
 *
 * React Query hooks for managing records and collections state.
 * Part of the V4 Records API.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collectionService } from '../services/collectionService';
import type {
  CollectionListResponse,
  CollectionWithStats,
  CollectionFilters,
  CollectionUpdateRequest,
  RecordsListResponse,
  RecordFilters,
  ConfirmComplaintResponse,
  ReclassifyRecordResponse,
  Collection,
} from '../services/types';
import type { Record } from '../types/record';
import { APIErrorException } from '../services/types/errors';

// =============================================================================
// Query Keys
// =============================================================================

/**
 * Query key factory for collections and records queries
 */
export const recordsKeys = {
  all: ['records'] as const,

  // Collections
  collections: () => [...recordsKeys.all, 'collections'] as const,
  collectionList: (filters?: CollectionFilters) =>
    [...recordsKeys.collections(), 'list', filters] as const,
  collectionDetail: (id: string) =>
    [...recordsKeys.collections(), 'detail', id] as const,

  // Records
  recordsInCollection: (collectionId: string) =>
    [...recordsKeys.all, 'in-collection', collectionId] as const,
  recordList: (collectionId: string, filters?: RecordFilters, page?: number, pageSize?: number) =>
    [...recordsKeys.recordsInCollection(collectionId), 'list', filters, page, pageSize] as const,
  recordDetail: (id: string) =>
    [...recordsKeys.all, 'detail', id] as const,
};

// =============================================================================
// Collection Hooks
// =============================================================================

/**
 * Hook to fetch collections with optional filters
 *
 * @param filters - Optional filters (type, status)
 * @returns React Query result with collections
 */
export const useCollections = (filters?: CollectionFilters) => {
  return useQuery<CollectionListResponse, Error>({
    queryKey: recordsKeys.collectionList(filters),
    queryFn: () => collectionService.list(filters),
    retry: (failureCount, error) => {
      if (error instanceof APIErrorException && error.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to fetch a single collection with statistics
 *
 * @param collectionId - UUID of the collection
 * @param options - Query options
 * @returns React Query result with collection and stats
 */
export const useCollection = (
  collectionId: string,
  options?: { enabled?: boolean }
) => {
  const { enabled = true } = options || {};

  return useQuery<CollectionWithStats, Error>({
    queryKey: recordsKeys.collectionDetail(collectionId),
    queryFn: () => collectionService.get(collectionId),
    enabled: enabled && !!collectionId,
    retry: (failureCount, error) => {
      if (error instanceof APIErrorException) {
        if (error.status === 404 || error.status === 401) {
          return false;
        }
      }
      return failureCount < 2;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to update collection detection settings
 *
 * @returns Mutation for updating collection settings
 */
export const useUpdateCollectionSettings = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Collection,
    Error,
    { collectionId: string; settings: CollectionUpdateRequest }
  >({
    mutationFn: ({ collectionId, settings }) =>
      collectionService.updateSettings(collectionId, settings),
    onSuccess: (data, variables) => {
      // Invalidate collection detail query
      queryClient.invalidateQueries({
        queryKey: recordsKeys.collectionDetail(variables.collectionId),
      });
      // Invalidate collection list
      queryClient.invalidateQueries({
        queryKey: recordsKeys.collections(),
      });
    },
  });
};

/**
 * Hook to rename a collection
 */
export const useRenameCollection = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Collection,
    Error,
    { collectionId: string; name: string }
  >({
    mutationFn: ({ collectionId, name }) =>
      collectionService.rename(collectionId, name),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: recordsKeys.collectionDetail(variables.collectionId),
      });
      queryClient.invalidateQueries({
        queryKey: recordsKeys.collections(),
      });
    },
  });
};

/**
 * Hook to pause or resume monitoring for a collection.
 */
export const useUpdateMonitoringStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Collection,
    Error,
    { collectionId: string; status: 'active' | 'paused' }
  >({
    mutationFn: ({ collectionId, status }) =>
      collectionService.updateMonitoringStatus(collectionId, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: recordsKeys.collectionDetail(variables.collectionId),
      });
      queryClient.invalidateQueries({
        queryKey: recordsKeys.collections(),
      });
    },
  });
};

/**
 * Hook to delete a collection
 */
export const useDeleteCollection = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { id: string; status: string },
    Error,
    { collectionId: string }
  >({
    mutationFn: ({ collectionId }) =>
      collectionService.delete(collectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: recordsKeys.collections(),
      });
    },
  });
};

// =============================================================================
// Records Hooks
// =============================================================================

/**
 * Hook to fetch records in a collection with pagination and filtering
 *
 * @param collectionId - UUID of the collection
 * @param filters - Optional filters
 * @param page - Page number (default 1)
 * @param pageSize - Items per page (default 50)
 * @param options - Query options
 * @returns React Query result with paginated records
 */
export const useRecords = (
  collectionId: string,
  filters?: RecordFilters,
  page: number = 1,
  pageSize: number = 50,
  options?: { enabled?: boolean }
) => {
  const { enabled = true } = options || {};

  return useQuery<RecordsListResponse, Error>({
    queryKey: recordsKeys.recordList(collectionId, filters, page, pageSize),
    queryFn: () => collectionService.listRecords(collectionId, filters, page, pageSize),
    enabled: enabled && !!collectionId,
    retry: (failureCount, error) => {
      if (error instanceof APIErrorException) {
        if (error.status === 404 || error.status === 401) {
          return false;
        }
      }
      return failureCount < 2;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData, // Keep previous data while loading
  });
};

/**
 * Hook to fetch a single record with full detail
 *
 * @param recordId - UUID of the record
 * @param options - Query options
 * @returns React Query result with record detail
 */
export const useRecord = (
  recordId: string,
  options?: { enabled?: boolean }
) => {
  const { enabled = true } = options || {};

  return useQuery<Record, Error>({
    queryKey: recordsKeys.recordDetail(recordId),
    queryFn: () => collectionService.getRecord(recordId),
    enabled: enabled && !!recordId,
    retry: (failureCount, error) => {
      if (error instanceof APIErrorException) {
        if (error.status === 404 || error.status === 401) {
          return false;
        }
      }
      return failureCount < 2;
    },
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to confirm a record as a formal complaint
 *
 * @returns Mutation for confirming complaint
 */
export const useConfirmComplaint = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ConfirmComplaintResponse,
    Error,
    { recordId: string; notes?: string }
  >({
    mutationFn: ({ recordId, notes }) =>
      collectionService.confirmComplaint(recordId, notes),
    onSuccess: (data, variables) => {
      // Invalidate record detail query
      queryClient.invalidateQueries({
        queryKey: recordsKeys.recordDetail(variables.recordId),
      });
      // Invalidate records list (status may have changed)
      queryClient.invalidateQueries({
        queryKey: recordsKeys.all,
      });
    },
  });
};

/**
 * Hook to reject/unconfirm a record as a complaint
 *
 * Removes the human complaint confirmation, reverting the signal to
 * ML-detected-only state.
 *
 * @returns Mutation for rejecting complaint
 */
export const useRejectComplaint = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { id: string; isConfirmedComplaint: boolean },
    Error,
    { recordId: string }
  >({
    mutationFn: ({ recordId }) =>
      collectionService.rejectComplaint(recordId),
    onSuccess: (data, variables) => {
      // Invalidate record detail query
      queryClient.invalidateQueries({
        queryKey: recordsKeys.recordDetail(variables.recordId),
      });
      // Invalidate records list (status may have changed)
      queryClient.invalidateQueries({
        queryKey: recordsKeys.all,
      });
    },
  });
};

/**
 * Hook to reclassify a record with AI
 *
 * Triggers async reclassification using the latest ML model.
 * Note: Classification happens asynchronously - results will be available
 * after the background task completes.
 *
 * @returns Mutation for reclassifying record
 */
export const useReclassifyRecord = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ReclassifyRecordResponse,
    Error,
    { recordId: string }
  >({
    mutationFn: ({ recordId }) =>
      collectionService.reclassifyRecord(recordId),
    onSuccess: (data, variables) => {
      // Invalidate record detail query so it refetches when classification completes
      // Note: For real-time updates, WebSocket integration would be needed
      queryClient.invalidateQueries({
        queryKey: recordsKeys.recordDetail(variables.recordId),
      });
    },
  });
};

/**
 * Hook to start a new FOS Outcome Simulation for a record
 *
 * Creates a simulation that analyzes the record against historical
 * FOS ombudsman decisions to predict likely outcome.
 *
 * @returns Mutation for starting simulation
 */
export const useStartSimulation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { id: string; signal_id: string; status: string; created_at: string },
    Error,
    { recordId: string }
  >({
    mutationFn: ({ recordId }) =>
      collectionService.startSimulation(recordId),
    onSuccess: (data, variables) => {
      // Invalidate record detail and simulations queries
      queryClient.invalidateQueries({
        queryKey: recordsKeys.recordDetail(variables.recordId),
      });
      queryClient.invalidateQueries({
        queryKey: recordsKeys.all,
      });
    },
  });
};

// =============================================================================
// Default Export
// =============================================================================

export default {
  // Keys
  keys: recordsKeys,

  // Collections
  useCollections,
  useCollection,
  useUpdateCollectionSettings,

  // Records
  useRecords,
  useRecord,
  useConfirmComplaint,
  useRejectComplaint,
  useReclassifyRecord,
  useStartSimulation,
};
